import { StateCreator, create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";

export interface StoreOptions<T> {
  /** Enable persistence to localStorage/sessionStorage */
  persist?: boolean;
  /** Custom storage key name (defaults to name_store) */
  persistKey?: string;
  /** Use sessionStorage instead of localStorage */
  sessionOnly?: boolean;
  /** Only persist specific fields */
  persistFields?: (keyof T)[];
  /** Exclude specific fields from persistence */
  excludeFields?: (keyof T)[];
}

/**
 * Creates a Zustand store with optional persistence and devtools
 * Simplifies store creation for product teams
 */
export const createStore = <T>(
  name: string,
  storeDefinition: StateCreator<T>,
  options: StoreOptions<T> = {}
) => {
  const storeName = options.persistKey || `${name}_store`;

  if (options.persist) {
    const storage = options.sessionOnly
      ? createJSONStorage(() => sessionStorage)
      : createJSONStorage(() => localStorage);

    // Create partialize function if field filtering is specified
    const persistOptions: any = {
      name: storeName,
      storage,
    };

    if (options.persistFields) {
      persistOptions.partialize = (state: T) =>
        Object.fromEntries(
          options.persistFields!.map((key) => [key, (state as any)[key]])
        );
    } else if (options.excludeFields) {
      persistOptions.partialize = (state: T) =>
        Object.fromEntries(
          Object.entries(state as any).filter(
            ([key]) => !options.excludeFields!.includes(key as keyof T)
          )
        );
    }

    return create<T>()(
      devtools(persist(storeDefinition, persistOptions), { name: storeName })
    );
  }

  // Non-persistent store with devtools
  return create<T>()(devtools(storeDefinition, { name: storeName }));
};

/**
 * Creates a session-only persistent store (convenience function)
 */
export const createSessionStore = <T>(
  name: string,
  storeDefinition: StateCreator<T>,
  options: Omit<StoreOptions<T>, "sessionOnly"> = {}
) =>
  createStore(name, storeDefinition, {
    ...options,
    persist: true,
    sessionOnly: true,
  });
